import { useState } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";

// Tabela Pitagórica verdadeira usada em numerologia
const tabelaNumerologia: Record<string, number> = {
  // 1: A, J, S
  'A': 1, 'J': 1, 'S': 1,
  // 2: B, K, T
  'B': 2, 'K': 2, 'T': 2,
  // 3: C, L, U
  'C': 3, 'L': 3, 'U': 3,
  // 4: D, M, V
  'D': 4, 'M': 4, 'V': 4,
  // 5: E, N, W
  'E': 5, 'N': 5, 'W': 5,
  // 6: F, O, X
  'F': 6, 'O': 6, 'X': 6,
  // 7: G, P, Y
  'G': 7, 'P': 7, 'Y': 7,
  // 8: H, Q, Z
  'H': 8, 'Q': 8, 'Z': 8,
  // 9: I, R
  'I': 9, 'R': 9
};

interface NumerologiaResult {
  numeroFinal: number;
  soma: number;
  digitos: number[];
  detalhes: string;
}

export const NumerologiaNome = () => {
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [resultados, setResultados] = useState<{
    milhares: string[];
    centenas: string[];
    dezenas: string[];
    duques: string[];
    ternos: string[];
    calcDetalhes: string;
    numeroFinal: number;
  } | null>(null);

  const calcularNumerologia = (nome: string): NumerologiaResult => {
    const nomeProcessado = nome.toUpperCase().replace(/[^A-Z]/g, '');
    const digitos: number[] = [];
    let soma = 0;
    
    for (const letra of nomeProcessado) {
      const valor = tabelaNumerologia[letra] || 0;
      digitos.push(valor);
      soma += valor;
    }

    let detalhes = `Nome: ${nomeProcessado}\n`;
    detalhes += `Valores: ${digitos.join(' + ')} = ${soma}\n`;

    const somaOriginal = soma;
    let somaAtual = soma;
    while (somaAtual > 9) {
      const digitsArray = somaAtual.toString().split('');
      const novoNumero = digitsArray.reduce((acc, digit) => acc + parseInt(digit, 10), 0);
      detalhes += `Redução: ${digitsArray.join(' + ')} = ${novoNumero}\n`;
      somaAtual = novoNumero;
    }

    return { numeroFinal: somaAtual, soma: somaOriginal, digitos, detalhes };
  };

  const gerarNumeroAleatorio = (min: number, max: number, usados: Set<number> = new Set()): number => {
    let tentativas = 0;
    let num: number;
    do {
      num = Math.floor(Math.random() * (max - min + 1)) + min;
      tentativas++;
    } while (usados.has(num) && tentativas < 100);
    usados.add(num);
    return num;
  };

  const gerarJogos = () => {
    console.log('🎯 INÍCIO gerarJogos() - função chamada');
    const nome = nomeCompleto.trim();
    console.log('📝 Nome recebido:', nome);
    
    if (nome === '') {
      console.log('❌ Nome vazio, mostrando erro');
      toast.error('Por favor, digite seu nome completo!');
      return;
    }

    try {
      console.log('✅ Iniciando cálculo de numerologia para:', nome);
      const { numeroFinal, soma, detalhes } = calcularNumerologia(nome);
      console.log('Numerologia calculada:', { numeroFinal, soma, detalhes });

      const base = soma;
      const usadosMilhar = new Set<number>();
      const usadosCentena = new Set<number>();
      const usadosDezena = new Set<number>();

      // MILHAR - 5 jogos
      const milhares: string[] = [];
      for (let i = 0; i < 5; i++) {
        let milhar = (base * (i + 1) + numeroFinal * 111 + gerarNumeroAleatorio(1, 100)) % 10000;
        while (usadosMilhar.has(milhar)) {
          milhar = gerarNumeroAleatorio(0, 9999, usadosMilhar);
        }
        usadosMilhar.add(milhar);
        milhares.push(milhar.toString().padStart(4, '0'));
      }

      // CENTENA - 5 jogos
      const centenas: string[] = [];
      for (let i = 0; i < 5; i++) {
        let centena = (base * (i + 1) + numeroFinal * 11 + gerarNumeroAleatorio(1, 50)) % 1000;
        while (usadosCentena.has(centena)) {
          centena = gerarNumeroAleatorio(0, 999, usadosCentena);
        }
        usadosCentena.add(centena);
        centenas.push(centena.toString().padStart(3, '0'));
      }

      // DEZENA - 10 dezenas únicas
      const dezenas: string[] = [];
      for (let i = 0; i < 10; i++) {
        let dezena = (base + numeroFinal * (i + 1) + gerarNumeroAleatorio(1, 30)) % 100;
        while (usadosDezena.has(dezena)) {
          dezena = gerarNumeroAleatorio(0, 99, usadosDezena);
        }
        usadosDezena.add(dezena);
        dezenas.push(dezena.toString().padStart(2, '0'));
      }

      // DUQUE DE DEZENA - 5 jogos (2 dezenas cada)
      const duques: string[] = [];
      for (let i = 0; i < 5; i++) {
        duques.push(`${dezenas[i * 2]} - ${dezenas[i * 2 + 1]}`);
      }

      // TERNO DE DEZENA - 3 jogos (3 dezenas cada)
      const ternos: string[] = [];
      ternos.push(`${dezenas[0]} - ${dezenas[1]} - ${dezenas[2]}`);
      ternos.push(`${dezenas[3]} - ${dezenas[4]} - ${dezenas[5]}`);
      ternos.push(`${dezenas[6]} - ${dezenas[7]} - ${dezenas[8]}`);

      console.log('Resultados gerados:', { milhares, centenas, dezenas, duques, ternos });
      
      setResultados({
        milhares,
        centenas,
        dezenas,
        duques,
        ternos,
        calcDetalhes: detalhes,
        numeroFinal
      });

      toast.success('Números gerados com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar números:', error);
      toast.error('Erro ao gerar números. Tente novamente.');
    }
  };

  const limparDados = () => {
    setNomeCompleto('');
    setResultados(null);
    toast.info('Dados limpos');
  };

  const compartilharImagem = async () => {
    if (!resultados) {
      toast.error('Por favor, gere os números primeiro!');
      return;
    }

    const resultsDiv = document.getElementById('resultados-numerologia');
    if (!resultsDiv) return;

    try {
      const canvas = await html2canvas(resultsDiv, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false
      });

      canvas.toBlob(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `jogo-bicho-${nomeCompleto.replace(/\s+/g, '-')}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Imagem baixada com sucesso!');
      });
    } catch (error) {
      toast.error('Erro ao gerar imagem');
      console.error(error);
    }
  };

  const compartilharPDF = () => {
    if (!resultados) {
      toast.error('Por favor, gere os números primeiro!');
      return;
    }

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      pdf.setFontSize(22);
      pdf.setTextColor(102, 126, 234);
      pdf.text('Jogo do Bicho - Nome Premiado', 105, 20, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Nome: ${nomeCompleto}`, 105, 30, { align: 'center' });
      
      const calcLines = resultados.calcDetalhes.split('\n').filter(line => line.trim());
      pdf.setFontSize(12);
      pdf.setTextColor(133, 100, 4);
      pdf.text('Cálculo Numerológico:', 20, 45);
      
      pdf.setFontSize(10);
      pdf.setTextColor(80, 80, 80);
      let yPos = 52;
      calcLines.forEach(line => {
        pdf.text(line, 20, yPos);
        yPos += 6;
      });

      yPos += 5;

      pdf.setFontSize(14);
      pdf.setTextColor(102, 126, 234);
      pdf.text('Milhar:', 20, yPos);
      yPos += 7;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.text(resultados.milhares.join('  -  '), 20, yPos);
      yPos += 10;

      pdf.setFontSize(14);
      pdf.setTextColor(102, 126, 234);
      pdf.text('Centena:', 20, yPos);
      yPos += 7;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      pdf.text(resultados.centenas.join('  -  '), 20, yPos);
      yPos += 10;

      pdf.setFontSize(14);
      pdf.setTextColor(102, 126, 234);
      pdf.text('Duque de Dezena:', 20, yPos);
      yPos += 7;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      resultados.duques.forEach(duque => {
        pdf.text(duque, 20, yPos);
        yPos += 6;
      });
      yPos += 4;

      pdf.setFontSize(14);
      pdf.setTextColor(102, 126, 234);
      pdf.text('Terno de Dezena:', 20, yPos);
      yPos += 7;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      resultados.ternos.forEach(terno => {
        pdf.text(terno, 20, yPos);
        yPos += 6;
      });
      yPos += 4;

      pdf.setFontSize(14);
      pdf.setTextColor(102, 126, 234);
      pdf.text('Dezena:', 20, yPos);
      yPos += 7;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      
      let linha = '';
      resultados.dezenas.forEach((dez, idx) => {
        linha += dez + '  ';
        if ((idx + 1) % 5 === 0 || idx === resultados.dezenas.length - 1) {
          pdf.text(linha, 20, yPos);
          yPos += 6;
          linha = '';
        }
      });

      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Gerado por Jogo do Bicho - Nome Premiado', 105, 285, { align: 'center' });

      pdf.save(`jogo-bicho-${nomeCompleto.replace(/\s+/g, '-')}.pdf`);
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
      console.error(error);
    }
  };

  return (
    <div className="space-y-6">
      <PageInstructions
        title="📖 Como usar o Nome Premiado"
        steps={[
          "Digite seu nome completo no campo indicado",
          "Clique em 'Calcular' para processar a numerologia",
          "O sistema converte cada letra em um número e soma tudo",
          "Veja seu número da sorte, grupo do bicho e dezenas correspondentes",
          "Use esses números nas suas apostas — são personalizados para você!"
        ]}
      />
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">🎰 Nome Premiado</CardTitle>
          <CardDescription>Números da Sorte baseados no seu Nome</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nomeCompleto">Digite seu nome completo:</Label>
            <Input
              id="nomeCompleto"
              type="text"
              value={nomeCompleto}
              onChange={(e) => setNomeCompleto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  gerarJogos();
                }
              }}
              placeholder="Ex: Maria da Silva Santos"
              autoComplete="off"
              className="text-base"
            />
          </div>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              gerarJogos();
            }} 
            type="button"
            size="lg"
            className="w-full sm:w-auto"
          >
            ✨ Gerar Números da Sorte
          </Button>
        </CardContent>
      </Card>

      {resultados && (
        <div id="resultados-numerologia" className="space-y-4">
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 border-l-4 border-amber-500">
            <CardHeader>
              <CardTitle className="text-lg text-amber-900 dark:text-amber-100">📊 Cálculo Numerológico</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-mono text-sm text-amber-800 dark:text-amber-200 whitespace-pre-line leading-relaxed">
                {resultados.calcDetalhes}
              </div>
              <div className="font-mono text-base font-bold text-amber-900 dark:text-amber-100 mt-3">
                Número da Sorte: {resultados.numeroFinal}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-primary">
            <CardHeader>
              <CardTitle className="text-lg">🎯 Milhar</CardTitle>
              <CardDescription>Números de 4 dígitos (0000 a 9999)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {resultados.milhares.map((num, idx) => (
                  <div key={idx} className="bg-primary/10 px-4 py-3 rounded-lg font-bold text-lg text-primary min-w-[80px] text-center">
                    {num}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-primary">
            <CardHeader>
              <CardTitle className="text-lg">💯 Centena</CardTitle>
              <CardDescription>Números de 3 dígitos (000 a 999)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {resultados.centenas.map((num, idx) => (
                  <div key={idx} className="bg-primary/10 px-4 py-3 rounded-lg font-bold text-lg text-primary min-w-[70px] text-center">
                    {num}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-primary">
            <CardHeader>
              <CardTitle className="text-lg">🎲 Duque de Dezena</CardTitle>
              <CardDescription>Combinações de 2 dezenas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {resultados.duques.map((num, idx) => (
                  <div key={idx} className="bg-primary/10 px-4 py-3 rounded-lg font-bold text-lg text-primary text-center">
                    {num}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-primary">
            <CardHeader>
              <CardTitle className="text-lg">🎪 Terno de Dezena</CardTitle>
              <CardDescription>Combinações de 3 dezenas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {resultados.ternos.map((num, idx) => (
                  <div key={idx} className="bg-primary/10 px-4 py-3 rounded-lg font-bold text-lg text-primary text-center">
                    {num}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-primary">
            <CardHeader>
              <CardTitle className="text-lg">🔢 Dezena</CardTitle>
              <CardDescription>Números de 2 dígitos (00 a 99)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {resultados.dezenas.map((num, idx) => (
                  <div key={idx} className="bg-primary/10 px-4 py-3 rounded-lg font-bold text-lg text-primary min-w-[60px] text-center">
                    {num}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={limparDados} 
              variant="destructive" 
              className="flex-1"
              size="lg"
            >
              🗑️ Limpar Dados
            </Button>
            <Button 
              onClick={compartilharPDF} 
              variant="secondary" 
              className="flex-1"
              size="lg"
            >
              📄 Baixar PDF
            </Button>
            <Button 
              onClick={compartilharImagem} 
              className="flex-1"
              size="lg"
            >
              🖼️ Baixar Imagem
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
